import { jsx, css } from '@emotion/react';
import styled from '@emotion/styled';
import { useEffect } from 'react';

import { ComparisonTable } from './ComparisonTable';
import { connectVariant } from './SprayfoamConnectors';
import { calculatorTracker } from '../CalculatorTracker.jsx';
import DownloadLink from '../OC/oc-download-link';

const calcLayout = css`
  display: grid;
  column-gap: 30px;
  row-gap: 24px;

  @media (min-width: 1200px) {
    grid-template-columns: 1fr 2fr;
  }
`;

const calcSidebar = css`
  background: #e6e6e6;
  padding: 25px 15px;

  @media (max-width: 1199px) {
    margin-left: -15px;
    margin-right: -15px;
  }

  input[type='radio'] + label {
    margin-right: 16px;
  }

  h3 {
    border-bottom: 1px solid #000;
    margin-bottom: 16px;
    padding-bottom: 4px;
  }

  > div {
    margin-bottom: 24px;
  }
`;

const footnotes = css`
  list-style: none;
  counter-reset: localCounter;
  font-size: 12px;
  padding-left: 0;

  li {
    counter-increment: localCounter;
    margin-bottom: 3px;

    &::before {
      content: counter(localCounter);
      font-size: 8px;
      margin-right: 3px;
      position: relative;
      bottom: 2px;
    }
  }
`;

const miniHeading = css`
  font-family: OCRoboto;
  font-size: 18px;
  font-weight: 700;
  line-height: 21px;
  text-transform: uppercase;
  margin-bottom: 4px;
  display: block;
`;

const bigPink = css`
  font-family: Oswald;
  font-size: 34px;
  font-weight: 500;
  line-height: 34px;
  text-align: left;
  color: #d40f7d;
  text-transform: uppercase;
  margin-bottom: 12px;
  display: block;
`;

const twoUp = css`
  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 30px;
    row-gap: 24px;
  }
`;

const Fieldset = styled.fieldset`
  padding: 0;
  border: 0;
  margin: 0;

  legend {
    width: 100%;
  }
`;

export const XSidebar = ({ variant, setVariant }) => (
  <div css={calcSidebar}>
    <div className="projectType">
      <Fieldset>
        <legend>
          <h3 className="body-font">Spray Foam Type</h3>
        </legend>
        <input
          name="spray_foam_type"
          type="radio"
          value="open_cell"
          id="open_cell"
          checked={variant === 'open_cell'}
          onChange={() => {
            setVariant('open_cell');
          }}
          data-track="toggle"
          data-track-option="Open Cell"
        />
        <label htmlFor="open_cell">Open Cell</label>
        <input
          name="spray_foam_type"
          type="radio"
          value="closed_cell"
          id="closed_cell"
          checked={variant === 'closed_cell'}
          onChange={() => {
            setVariant('closed_cell');
          }}
          data-track="toggle"
          data-track-option="Closed Cell"
        />
        <label htmlFor="closed_cell">Closed Cell</label>
      </Fieldset>
    </div>
  </div>
);

export const Sidebar = connectVariant(XSidebar);

export const XCalcSelector = ({ variant }) => {
  if (variant === 'open_cell') {
    return <CalcOpenCell />;
  }
  return <CalcClosedCell />;
};

export const CalcSelector = connectVariant(XCalcSelector);

export const XCalculatorSprayFoam = ({ variant, logData }) => {
  useEffect(() => {
    logData({
      variant,
    });
  }, [variant]);

  return (
    <>
      <div className="header">
        <h5 className="prehead">PROPINK® Savings Calculator</h5>
        <h2>Calculate your Potential</h2>

        <p>
          Simplify your life and build your business by making the smart choice. ProPink® L77 Loosefill Insulation
          brings increased coverage, faster applications and the highest rated R-value per square inch to every job. Use
          the calculator below to see the difference between ProPink® L77 and competitor products.
        </p>
      </div>
      <div css={calcLayout}>
        <Sidebar />
        <div>
          <CalcSelector />
        </div>
      </div>
    </>
  );
};

export const CalculatorSprayFoam = calculatorTracker('sprayfoam', connectVariant(XCalculatorSprayFoam));

export const CalcOpenCell = () => (
  <ComparisonTable>
    <table className="threeUp">
      <thead>
        <tr>
          <th>
            <span className="sr-only">Variables</span>
          </th>
          <th className="colProPink">Owens Corning ProPink L77</th>
          <th>Spray Foam (Open Cell)</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="tableSubhead" colSpan="3">
            Initial Investment
          </td>
        </tr>
        <tr>
          <td className="tableIndent">Initial Machine Investment</td>
          <td className="center">$78,000&sup1;</td>
          <td className="center">$115,000&sup2;</td>
        </tr>
        <tr>
          <td className="tableSubhead" colSpan="3">
            Annual Material and Labor Costs
          </td>
        </tr>
        <tr>
          <td className="tableIndent">Material Cost</td>
          <td className="center">$1,050,000&sup3;</td>
          <td className="center">$360,000&#x2074;</td>
        </tr>
        <tr>
          <td className="tableIndent">Labor Cost</td>
          <td className="center">$90,000&#x2075;</td>
          <td className="center">$94,500&#x2076;</td>
        </tr>
        <tr>
          <td className="tableIndent">Total Costs</td>
          <td className="center">$1,140,000</td>
          <td className="center">$454,000</td>
        </tr>
        <tr>
          <td className="tableSubhead" colSpan="3">
            Revenue and Return on Investment
          </td>
        </tr>
        <tr>
          <td className="tableIndent">Annual Revenue</td>
          <td className="center colProPink">
            $2,025,000&#x2077;
            <br />
            86% More
          </td>
          <td className="center">$1,085,000&#x2078;</td>
        </tr>
        <tr>
          <td className="tableIndent">Annual Total Costs</td>
          <td className="center colProPink">$1,140,000</td>
          <td className="center">$454,500</td>
        </tr>
        <tr>
          <td className="tableSubhead">Total Margin</td>
          <td className="center colProPink">
            $885,000
            <br />
            40% More
          </td>
          <td className="center">$630,500</td>
        </tr>
        <tr>
          <td className="tableSubhead">Payback on Initial Investment</td>
          <td className="center colProPink">11x</td>
          <td className="center">5x</td>
        </tr>
      </tbody>
    </table>

    <DownloadLink
      href="http://owenscorning.com/dms/10023967"
      fileType="pdf"
      data-track="file-download"
      data-track-file-name="ProPink L77 vs. Spray Foam Comparison Chart - Open Cell"
      data-track-file-type="PDF"
      data-track-doc-type="Brochure"
      text="ProPink L77 vs. Spray Foam Comparison Chart "
    >
      PDF | 284 KB
    </DownloadLink>

    <div css={twoUp}>
      <p>
        <span css={miniHeading}>Annual savings</span>
        <span css={bigPink}>Up to $885,000</span>
      </p>

      <p>
        <span css={miniHeading}>Payback on initial investment</span>
        <span css={bigPink}>Up to 11X</span>
      </p>
    </div>

    <a
      target="_blank"
      className="oc-cta-button"
      style={{ marginBottom: '15px' }}
      href="/dms/10023967"
      data-track="file-download"
      data-track-file-name="ProPink L77 vs. Spray Foam Comparison Chart - Open Cell"
      data-track-file-type="PDF"
      data-track-doc-type="Brochure"
    >
      Download Comparison Chart
    </a>

    <ol css={footnotes}>
      <li>Includes $33,000 for blowing machine and $45,000 for box truck</li>
      <li>
        Includes $11,500 for trailer, $33,500 for truck, $42,000 for components (estimate for compressor, generator,
        hoses, spray gun and accessories), and $28,000 for rig
      </li>
      <li>Based on up to 150 bags of ProPink L77 per day at $28 per bag – $4,200 × 5 days × 50 weeks</li>
      <li>Average rig goes through one material set of open cell per day = $1,800/day × 4 days × 50 weeks</li>
      <li>Two installers at $180 per day gross pay for each installer (nine hour days)</li>
      <li>National average is $25/hour for sprayer and $16/hour for helper; $370/day for both (nine hour days)</li>
      <li>
        Based on blowing attics at R-49 – 150 bags × 45 sq ft per bag × national average of $1.20 per sq ft = $8,100 × 5
        days × 50 weeks
      </li>
      <li>
        Winter and summer average BF per set of 17,500 × national average of $.31 per BF × 4 days per week (assumes 20%
        time or 1 day per week for maintenance) × 50 weeks
      </li>
    </ol>
  </ComparisonTable>
);

export const CalcClosedCell = () => (
  <ComparisonTable>
    <table className="threeUp">
      <thead>
        <tr>
          <th />
          <th className="colProPink">Owens Corning ProPink L77</th>
          <th>Spray Foam (Closed Cell)</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="tableSubhead" colSpan="3">
            Initial Investment
          </td>
        </tr>
        <tr>
          <td className="tableIndent">Initial Machine Investment</td>
          <td className="center">$78,000&sup1;</td>
          <td className="center">$115,000&sup2;</td>
        </tr>
        <tr>
          <td className="tableSubhead" colSpan="3">
            Annual Material and Labor Costs
          </td>
        </tr>
        <tr>
          <td className="tableIndent">Material Cost</td>
          <td className="center">$1,050,000&sup3;</td>
          <td className="center">$390,000&#x2074;</td>
        </tr>
        <tr>
          <td className="tableIndent">Labor Cost</td>
          <td className="center">$90,000&#x2075;</td>
          <td className="center">$94,500&#x2076;</td>
        </tr>
        <tr>
          <td className="tableIndent">Total Costs</td>
          <td className="center">$1,140,000</td>
          <td className="center">$484,500</td>
        </tr>
        <tr>
          <td className="tableSubhead" colSpan="3">
            Revenue and Return on Investment
          </td>
        </tr>
        <tr>
          <td className="tableIndent">Annual Revenue</td>
          <td className="center colProPink">
            $2,025,000&#x2077;
            <br />
            131% More
          </td>
          <td className="center">$874,000&#x2078;</td>
        </tr>
        <tr>
          <td className="tableIndent">Annual Total Costs</td>
          <td className="center colProPink">$1,140,000</td>
          <td className="center">$484,500</td>
        </tr>
        <tr>
          <td className="tableSubhead">Total Margin</td>
          <td className="center colProPink">
            $885,000
            <br />
            127% More
          </td>
          <td className="center">$389,500</td>
        </tr>
        <tr>
          <td className="tableSubhead">Payback on Initial Investment</td>
          <td className="center colProPink">11x</td>
          <td className="center">3x</td>
        </tr>
      </tbody>
    </table>

    <DownloadLink
      href="http://owenscorning.com/dms/10024076"
      fileType="pdf"
      data-track="file-download"
      data-track-file-name="ProPink L77 vs. Spray Foam Comparison Chart - Closed Cell"
      data-track-file-type="PDF"
      data-track-doc-type="Brochure"
      text="ProPink L77 vs. Spray Foam Comparison Chart "
    >
      PDF | 283 KB
    </DownloadLink>

    <div css={twoUp}>
      <p>
        <span css={miniHeading}>Annual savings</span>
        <span css={bigPink}>Up to $885,000</span>
      </p>

      <p>
        <span css={miniHeading}>Payback on initial investment</span>
        <span css={bigPink}>Up to 11X</span>
      </p>
    </div>

    <a target="_blank" className="oc-cta-button" style={{ marginBottom: '15px' }} href="/dms/10024076">
      Download Comparison Chart
    </a>

    <ol css={footnotes}>
      <li>Includes $33,000 for blowing machine and $45,000 for box truck</li>
      <li>
        Includes $11,500 for trailer, $33,500 for truck, $42,000 for components (estimate for compressor, generator,
        hoses, spray gun and accessories), and $28,000 for rig
      </li>
      <li>Based on up to 150 bags of ProPink L77 per day at $28 per bag – $4,200 × 5 days × 50 weeks</li>
      <li>Average rig goes through one material set of open cell per day = $1,950/day × 4 days × 50 weeks</li>
      <li>Two installers at $180 per day gross pay for each installer (nine hour days)</li>
      <li>National average is $25/hour for sprayer and $16/hour for helper; $370/day for both (nine hour days)</li>
      <li>
        Based on blowing attics at R-49 – 150 bags × 45 sq ft per bag × national average of $1.20 per sq ft = $8,100 × 5
        days × 50 weeks
      </li>
      <li>
        Winter and summer average BF per set of 17,500 × national average of $.31 per BF × 4 days per week (assumes 20%
        time or 1 day per week for maintenance) × 50 weeks
      </li>
    </ol>
  </ComparisonTable>
);
